import {
  FinancialInstitutionCategory,
  useSearchFinancialInstitutionsQuery,
} from '@/store/financialInstitutionsApi';
import { SelectChangeEvent } from '@mui/material';
import { useState, useMemo, useCallback } from 'react';

type Category = FinancialInstitutionCategory | 'All';

interface FastLinkInstitution {
  label: string;
  value: string;
  url: string;
  isPopular: boolean;
  isDisabled: boolean;
  disabledMessage: string | null;
  categories: Category[];
  // Map to FastLink institution format
  institution: {
    InstitutionId: string;
    FinancialInstitutionId: string;
    Name: string;
    BaseUrl: string;
  };
}

const useFilteredResults = () => {
  const [apiSearchTerm, setApiSearchTerm] = useState('');
  const [institutionSearch, setInstitutionSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<Category>('All');

  const { data: searchApiResult, isLoading: isSearchLoading } =
    useSearchFinancialInstitutionsQuery(
      { searchTerm: apiSearchTerm },
      { skip: !apiSearchTerm },
    );

  /**
   * Users can filter by an additional "Institution Type".
   * In legacy, the "tab" is only displayed if there are
   * items that match that tab.
   */
  const categoryList: Category[] = useMemo(() => {
    const flattened = searchApiResult?.Records.flatMap(
      (record) => record.Categories,
    );
    const deduplicate = Array.from(new Set(flattened));

    if (deduplicate.length > 1) {
      return ['All', ...deduplicate];
    }

    return [];
  }, [searchApiResult]);

  /**
   * This follows the legacy pattern.
   */
  const results: FastLinkInstitution[] = useMemo(() => {
    if (apiSearchTerm && searchApiResult) {
      return searchApiResult.Records.map((record) => ({
        label: record.Name,
        value: record.FinancialInstitutionId.toString(),
        url: record.BaseUrl,
        isPopular: record.IsPopular,
        isDisabled: record.IsDisabled,
        disabledMessage: record.DisabledMessage,
        categories: record.Categories,
        // Map to FastLink institution format
        institution: {
          InstitutionId: record.InstitutionId.toString(),
          FinancialInstitutionId: record.FinancialInstitutionId.toString(),
          Name: record.Name,
          BaseUrl: record.BaseUrl,
        },
      }));
    }
    return [];
  }, [apiSearchTerm, searchApiResult]);

  const filterByUrl = useCallback(
    (result: FastLinkInstitution) => {
      return result.url.toLowerCase().includes(institutionSearch.toLowerCase());
    },
    [institutionSearch],
  );

  const filterByName = useCallback(
    (result: FastLinkInstitution) => {
      return result.label
        .toLowerCase()
        .includes(institutionSearch.toLowerCase());
    },
    [institutionSearch],
  );

  /**
   * Search by Name or Url.
   */
  const filteredResults: FastLinkInstitution[] = useMemo(() => {
    if (!institutionSearch) {
      return results;
    }
    return results.filter((result) => {
      return filterByUrl(result) || filterByName(result);
    });
  }, [filterByName, filterByUrl, institutionSearch, results]);

  /**
   * Display two items in the "Top Results" section.
   */
  const topResults = useMemo(() => {
    const popular = filteredResults.filter((item) => item.isPopular);
    const others = filteredResults.filter((item) => !item.isPopular);
    return [...popular, ...others].slice(0, 2);
  }, [filteredResults]);

  /**
   * Additional filtering by "Institution Type" (FinancialInstitutionCategory)
   */
  const filteredByCategory = useMemo(() => {
    if (categoryFilter === 'All') {
      return filteredResults;
    }

    return filteredResults.filter((result) => {
      return result.categories.includes(categoryFilter);
    });
  }, [categoryFilter, filteredResults]);

  const handleInstitutionTypeChange = (event: SelectChangeEvent) => {
    setCategoryFilter(event.target.value as Category);
  };

  const handleSearchChange = (value: string) => {
    setInstitutionSearch(value);
    setApiSearchTerm(value);
  };

  return {
    handleSearchChange,
    institutionSearch,
    isSearchLoading,
    filteredResults,
    topResults,
    categoryFilter,
    handleInstitutionTypeChange,
    categoryList,
    filteredByCategory,
  };
};

export default useFilteredResults;
